
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Country, VisaRequirement } from '@/components/visa/types';
import { useAuthSession } from '@/hooks/use-auth-session';

export const useVisaData = (selectedCountry: string | null) => {
  const session = useAuthSession();

  const { data: originCountries, isLoading: isLoadingCountries } = useQuery({
    queryKey: ['origin_countries'],
    enabled: !!session,
    queryFn: async () => {
      console.log('Fetching origin countries...');
      
      const { data: countries, error } = await supabase
        .from('countries')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Countries query error:', error);
        throw error;
      }

      console.log('Available countries:', countries);
      return countries as Country[];
    },
  });

  const { data: visaRequirements, isLoading: isLoadingVisa } = useQuery({
    queryKey: ['visa_requirements', selectedCountry],
    enabled: !!session && !!selectedCountry,
    queryFn: async () => {
      console.log('Fetching visa requirements for origin country:', selectedCountry);
      
      const { data, error } = await supabase
        .from('visa_requirements')
        .select(`
          id,
          origin_country,
          destination_country,
          visa_type,
          max_stay_days,
          notes,
          destination_country_details:countries!visa_requirements_destination_country_fkey(*)
        `)
        .eq('origin_country', selectedCountry);
      
      if (error) {
        console.error('Visa requirements query error:', error);
        throw error;
      }

      console.log('Raw visa requirements data:', data);

      // Transform and sort the data
      const transformedData = data
        .filter(req => req.destination_country_details)
        .map(req => {
          // Get the destination country details
          const destDetails = Array.isArray(req.destination_country_details) 
            ? req.destination_country_details[0] 
            : req.destination_country_details;
          
          // Add region information to each country
          return {
            id: req.id,
            origin_country: req.origin_country,
            destination_country: req.destination_country,
            visa_type: req.visa_type,
            max_stay_days: req.max_stay_days,
            notes: req.notes,
            destination_country_details: {
              ...destDetails,
              region: getRegionForCountry(destDetails.name)
            }
          };
        }) as VisaRequirement[];

      // Sort the data: visa_free first, then alphabetically by destination country name
      const sortedData = transformedData.sort((a, b) => {
        // First, sort by visa type (visa_free comes first)
        if (a.visa_type === 'visa_free' && b.visa_type !== 'visa_free') return -1;
        if (a.visa_type !== 'visa_free' && b.visa_type === 'visa_free') return 1;
        
        // Then sort alphabetically by destination country name
        return a.destination_country_details.name.localeCompare(b.destination_country_details.name);
      });

      console.log('Transformed and sorted visa requirements:', sortedData);
      return sortedData;
    },
  });

  // Helper function to determine region based on country name
  const getRegionForCountry = (countryName: string): string => {
    const europeanCountries = ['France', 'Germany', 'Italy', 'Spain', 'United Kingdom', 'Portugal', 'Switzerland', 'Sweden', 'Norway', 'Finland', 'Denmark', 'Netherlands', 'Belgium', 'Austria', 'Greece', 'Ireland', 'Poland', 'Czech Republic', 'Hungary', 'Croatia'];
    const asianCountries = ['Japan', 'China', 'India', 'Thailand', 'Vietnam', 'Indonesia', 'Malaysia', 'Singapore', 'Philippines', 'South Korea', 'Taiwan', 'Hong Kong', 'Cambodia', 'Myanmar', 'Laos', 'Nepal', 'Bhutan', 'Sri Lanka', 'Bangladesh', 'Pakistan'];
    const northAmericanCountries = ['United States', 'Canada', 'Mexico', 'Costa Rica', 'Panama', 'Jamaica', 'Cuba', 'Dominican Republic', 'Haiti', 'Bahamas'];
    const southAmericanCountries = ['Brazil', 'Argentina', 'Colombia', 'Peru', 'Chile', 'Ecuador', 'Bolivia', 'Venezuela', 'Uruguay', 'Paraguay', 'Guyana', 'Suriname'];
    const africanCountries = ['South Africa', 'Egypt', 'Morocco', 'Kenya', 'Tanzania', 'Nigeria', 'Ghana', 'Ethiopia', 'Rwanda', 'Uganda', 'Senegal', 'Tunisia', 'Botswana', 'Namibia', 'Zimbabwe', 'Zambia', 'Mozambique', 'Madagascar'];
    const middleEasternCountries = ['Israel', 'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Bahrain', 'Oman', 'Kuwait', 'Jordan', 'Lebanon', 'Turkey', 'Iran', 'Iraq'];
    const oceaniaCountries = ['Australia', 'New Zealand', 'Fiji', 'Papua New Guinea', 'Solomon Islands', 'Vanuatu', 'Samoa', 'Tonga'];

    if (europeanCountries.some(country => countryName.includes(country))) return 'Europe';
    if (asianCountries.some(country => countryName.includes(country))) return 'Asia';
    if (northAmericanCountries.some(country => countryName.includes(country))) return 'North America';
    if (southAmericanCountries.some(country => countryName.includes(country))) return 'South & Central America';
    if (africanCountries.some(country => countryName.includes(country))) return 'Africa';
    if (middleEasternCountries.some(country => countryName.includes(country))) return 'Middle East';
    if (oceaniaCountries.some(country => countryName.includes(country))) return 'Oceania';
    
    return 'Other';
  };

  return {
    originCountries,
    visaRequirements,
    isLoadingCountries,
    isLoadingVisa,
    isAuthenticated: !!session,
  };
};
